/**
 * AddAttachmentButton Component
 *
 * A compact inline button for adding attachments with dropdown options to either
 * upload a new file or choose from the document library.
 *
 * @example
 * ```jsx
 * <AddAttachmentButton
 *   onUpload={handleFileUpload}
 *   onLibrarySelect={handleLibrarySelect}
 *   isUploading={isUploading}
 *   buttonText="Add Attachment"
 * />
 * ```
 */

import { useState, useRef } from 'react';
import Compressor from 'compressorjs';
import { PlusIcon } from '@heroicons/react/20/solid';
import { ArrowUpTrayIcon, FolderOpenIcon } from '@heroicons/react/24/outline';
import useModal from '../../hooks/useModal';

const classNames = (...classes) => classes.filter(Boolean).join(' ');

const AddAttachmentButton = ({
	onUpload,
	showLibraryOption = true,
	isUploading = false,
	disabled = false,
	buttonText = 'Add Attachment',
	uploadText = 'Upload File',
	libraryText = 'Choose from Library',
	acceptTypes = 'image/jpeg, image/png, application/pdf, .doc, .docx, .xls, .xlsx',
	multiple = false,
	compressImages = true,
	compressionOptions = {
		quality: 0.6,
		maxWidth: 1920,
		maxHeight: 1920,
	},
	libraryMultiSelect = true,
	className = '',
}) => {
	const [showUploadOptions, setShowUploadOptions] = useState(false);
	const [isProcessing, setIsProcessing] = useState(false);
	const fileInputRef = useRef(null);
	const { setOpen: openDocumentPicker } = useModal('documentPicker');

	const handleFileChange = async (e) => {
		const files = Array.from(e.target.files || []);
		if (files.length === 0) return;

		setIsProcessing(true);

		try {
			const processedFiles = [];

			for (const file of files) {
				// Compress images if enabled
				if (compressImages && file.type.startsWith('image/')) {
					const compressedFile = await new Promise((resolve) => {
						new Compressor(file, {
							...compressionOptions,
							success: (compressedBlob) => {
								resolve(
									new File([compressedBlob], file.name, {
										type: compressedBlob.type,
									}),
								);
							},
							error: () => {
								resolve(file); // Use original if compression fails
							},
						});
					});
					processedFiles.push(compressedFile);
				} else {
					processedFiles.push(file);
				}
			}

			if (onUpload) {
				await onUpload(processedFiles);
			}
		} catch (err) {
			console.error('Error processing files:', err);
		}

		setIsProcessing(false);

		// Reset input
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	const handleUploadClick = () => {
		fileInputRef.current?.click();
		setShowUploadOptions(false);
	};

	const handleLibrarySelect = () => {
		setShowUploadOptions(false);
		openDocumentPicker({
			multiSelect: libraryMultiSelect,
			onSelect: (selectedDocs) => {
				if (selectedDocs?.length > 0 && onUpload) {
					// Pass documents with fromLibrary flag
					const libraryFiles = selectedDocs.map((doc) => ({
						id: doc.id,
						name: doc.name || doc.fileName || 'document',
						size: doc.size || 0,
						type: doc.fileType || doc.contentType || 'application/octet-stream',
						fromLibrary: true,
					}));
					onUpload(libraryFiles);
				}
			},
		});
	};

	const isDisabled = disabled || isUploading || isProcessing;
	const buttonLabel = isUploading
		? 'Uploading...'
		: isProcessing
			? 'Processing...'
			: buttonText;

	return (
		<div className={classNames('relative', className)}>
			<button
				type="button"
				onClick={() =>
					showLibraryOption
						? setShowUploadOptions(!showUploadOptions)
						: handleUploadClick()
				}
				disabled={isDisabled}
				className={classNames(
					'inline-flex items-center gap-1 text-xs font-medium',
					isDisabled
						? 'text-gray-400 cursor-not-allowed'
						: 'text-indigo-600 hover:text-indigo-700',
				)}
			>
				<PlusIcon className="w-3.5 h-3.5" />
				{buttonLabel}
			</button>

			{/* Upload Options Dropdown */}
			{showLibraryOption && showUploadOptions && !isDisabled && (
				<>
					<div
						className="fixed inset-0 z-40"
						onClick={() => setShowUploadOptions(false)}
					/>
					<div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
						<button
							type="button"
							onClick={handleUploadClick}
							className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg"
						>
							<ArrowUpTrayIcon className="w-4 h-4 text-gray-500" />
							{uploadText}
						</button>
						<button
							type="button"
							onClick={handleLibrarySelect}
							className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-b-lg border-t border-gray-100"
						>
							<FolderOpenIcon className="w-4 h-4 text-gray-500" />
							{libraryText}
						</button>
					</div>
				</>
			)}

			{/* Hidden file input */}
			<input
				ref={fileInputRef}
				type="file"
				accept={acceptTypes}
				multiple={multiple}
				onChange={handleFileChange}
				className="hidden"
				disabled={isDisabled}
			/>
		</div>
	);
};

export default AddAttachmentButton;
